import React from "react";
import { BookingCard } from "./BookingCard";
import { BlockOutCard } from "./BlockOutCard";
import { type Booking, type BlockOut } from "../scheduleData";

interface CalendarDayCellProps {
  day: number;
  dateString: string;
  dayBookings: Booking[];
  dayBlockOuts: BlockOut[];
  isToday: boolean;
  serviceColors: Record<string, string>;
  serviceColorsFromData?: Record<string, string>;
  onCreateBooking: (bookingData: any) => void;
  onEditBooking: (bookingId: string, bookingData: any) => void;
  onDeleteBooking: (bookingId: string) => void;
  onEditBlockOut: (blockOutId: string, data: any, otherPharmacyId?: string) => void;
  onDeleteBlockOut: (blockOutId: string, otherPharmacyId?: string) => void;
  onViewDay: (dateString: string) => void;
  onCreateBookingForDate: (dateString: string) => void;
  pharmacistNames: string[];
  rooms: string[];
  readOnlyBookings?: boolean;
  isDarkMode?: boolean;
}

export function CalendarDayCell({
  day,
  dateString,
  dayBookings,
  dayBlockOuts,
  isToday,
  serviceColors,
  serviceColorsFromData = {},
  onCreateBooking,
  onEditBooking,
  onDeleteBooking,
  onEditBlockOut,
  onDeleteBlockOut,
  onViewDay,
  onCreateBookingForDate,
  pharmacistNames,
  rooms,
  readOnlyBookings = false,
  isDarkMode = false,
}: CalendarDayCellProps) {
  const handleCellClick = (e: React.MouseEvent) => {
    // Only show create modal if clicking on empty space
    const target = e.target as HTMLElement;
    const isBookingCard = target.closest("[data-booking-card]");
    const isBlockOutCard = target.closest("[data-blockout-card]");
    const isMoreButton = target.closest("[data-more-button]");

    // Disallow creating bookings on past dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clicked = new Date(`${dateString}T00:00:00`);
    if (clicked.getTime() < today.getTime()) {
      return;
    }

    if (!isBookingCard && !isBlockOutCard && !isMoreButton) {
      onCreateBookingForDate(dateString);
    }
  };

  return (
    <div
      className={`h-36 p-2 border-r border-b cursor-pointer transition-colors duration-300 flex flex-col overflow-hidden ${
        isDarkMode
          ? `${isToday ? "bg-blue-900 hover:bg-blue-800" : "bg-gray-800 hover:bg-gray-700"} border-gray-600`
          : `${isToday ? "bg-blue-50 hover:bg-blue-100" : "bg-white hover:bg-gray-50"} border-gray-200`
      }`}
      onClick={handleCellClick}
    >
      <div
        className={`text-sm font-medium mb-1 flex-shrink-0 transition-colors duration-300 ${
          isToday
            ? "text-blue-600"
            : isDarkMode
              ? "text-white"
              : "text-gray-900"
        }`}
      >
        {day}
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Calculate how many items to show based on whether we need a +more button */}
        {(() => {
          const totalItems = dayBookings.length + dayBlockOuts.length;
          const maxDisplayItems = 3;
          const hasMoreItems = totalItems > maxDisplayItems;
          const maxBlockOutsToShow = hasMoreItems
            ? Math.min(dayBlockOuts.length, 2)
            : dayBlockOuts.length;
          const maxBookingsToShow = Math.max(0, maxDisplayItems - maxBlockOutsToShow);

          return (
            <>
              <div className="space-y-1 flex-1 min-h-0 overflow-hidden">
                {dayBookings.slice(0, maxBookingsToShow).map((booking) => (
                  <div
                    key={booking.id}
                    data-booking-card
                    className="flex-shrink-0"
                  >
                    <BookingCard
                      booking={booking}
                      serviceColors={serviceColors}
                      serviceColorsFromData={serviceColorsFromData}
                      onEdit={onEditBooking}
                      onDelete={onDeleteBooking}
                      pharmacistNames={pharmacistNames}
                      rooms={rooms}
                      size="medium"
                      isDarkMode={isDarkMode}
                      isReadOnly={readOnlyBookings}
                    />
                  </div>
                ))}

                {/* Block-out periods */}
                {dayBlockOuts.slice(0, maxBlockOutsToShow).map((blockOut) => (
                  <div
                    key={blockOut.id}
                    data-blockout-card
                    className="flex-shrink-0"
                  >
                    <BlockOutCard
                      blockOut={blockOut}
                      onEdit={onEditBlockOut}
                      onDelete={onDeleteBlockOut}
                      size="medium"
                      isDarkMode={isDarkMode}
                    />
                  </div>
                ))}
              </div>

              {hasMoreItems && (
                <div
                  data-more-button
                  className={`text-xs text-center cursor-pointer hover:opacity-80 pointer-events-auto transition-colors duration-300 flex-shrink-0 py-1 mt-1 ${
                    isDarkMode
                      ? "text-gray-400 hover:text-gray-300"
                      : "text-gray-500 hover:text-gray-600"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDay(dateString);
                  }}
                >
                  +
                  {Math.max(dayBookings.length - maxBookingsToShow, 0) +
                    Math.max(dayBlockOuts.length - maxBlockOutsToShow, 0)}{" "}
                  more
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
